import { useStore } from 'effector-react'
import { createMediaStyles } from 'lib/styled-helpers'
import React, { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Button } from 'ui/button'
import { Checkbox } from 'ui/checkbox'
import { Input } from 'ui/input'

import { getProjectName } from '../../../lib/get-project-name'
import { useAVNConfig } from '../../../stages/AVN'
import { $subscribeChangesCitiesInHeader, unsubscribeChangesCitiesInHeader } from '../model'
import { CitySelectProps } from './index'

const MAX_CITIES = 4

export type Props = {
  cities: string[]
  selectedCities: string[]
  onSubmit: (selectedCities: string[]) => void
  i18n: CitySelectProps['i18n']
  visible: boolean
}

export const CitySelectForm: FC<Props> = ({ cities, selectedCities, onSubmit, i18n, visible }) => {
  const [showAllCities, setShowAllCities] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [allCitiesSelected, setAllCitiesSelected] = useState(false)
  const pureQuery = searchQuery.trim().toLowerCase()
  const [selectedCitiesState, setSelectedCitiesState] = useState(selectedCities)
  const { citySelect } = useAVNConfig()

  useEffect(() => {
    setSelectedCitiesState(selectedCities)
  }, [selectedCities])

  useEffect(() => {
    if (selectedCitiesState.length === cities.length) setAllCitiesSelected(true)
  }, [selectedCitiesState])

  useEffect(() => {
    if (allCitiesSelected) {
      setSelectedCitiesState([])
    }
  }, [allCitiesSelected])

  useEffect(() => {
    if (!visible && selectedCities.length !== selectedCitiesState.length) {
      setSelectedCitiesState(selectedCities)
      setAllCitiesSelected(false)
    } else {
      let arr: string[] = []
      selectedCities.forEach((value) => {
        selectedCitiesState.find((val) => {
          return value !== val ? arr.push(val) : null
        })
      })
      if (arr.length !== 0) {
        setSelectedCitiesState(selectedCities)
        setAllCitiesSelected(false)
      }
    }
  }, [visible])

  const toggleCitySelect = (city: string) => {
    if (selectedCitiesState.includes(city)) {
      setSelectedCitiesState((selectedCities) => selectedCities.filter((c) => c !== city))
    } else {
      setAllCitiesSelected(false)
      setSelectedCitiesState((selectedCities) => [...selectedCities, city])
    }
  }

  const filteredCities = useMemo(() => {
    if (!pureQuery) return [...selectedCities, ...cities.filter((city) => !selectedCities.includes(city))]
    const filteredCities = cities.filter(
      (city) => city.toLowerCase().includes(pureQuery) && !selectedCitiesState.includes(city)
    )
    return [...selectedCitiesState, ...filteredCities]
  }, [pureQuery, cities, selectedCitiesState, selectedCities])

  const visibleCities = useMemo(() => {
    return showAllCities ? filteredCities : filteredCities.slice(0, MAX_CITIES)
  }, [filteredCities, showAllCities])

  return (
    <Wrapper isTitle={citySelect?.i18n.selectYourCity}>
      <InputWrapper>
        <Input block value={searchQuery} onChange={setSearchQuery} label={i18n.findYourCity} />
      </InputWrapper>
      {filteredCities.length > 1 && (
        <AllCitiesWrapper>
          <Checkbox
            size="medium"
            checked={selectedCitiesState.length !== 0 ? allCitiesSelected : true}
            onChange={(value) => setAllCitiesSelected(value)}
            label={<AllCitiesLabel>{i18n.allCities}</AllCitiesLabel>}
          />
        </AllCitiesWrapper>
      )}
      {filteredCities.length > 0 && (
        <GroupFields>
          {visibleCities.map((city) => {
            return (
              <Field key={city}>
                <Checkbox
                  checked={selectedCitiesState.includes(city)}
                  label={city}
                  onChange={() => toggleCitySelect(city)}
                />
              </Field>
            )
          })}
          {!pureQuery && !showAllCities && cities.length > MAX_CITIES && (
            <MoreButton onClick={() => setShowAllCities(true)}>{i18n.showMoreCities}</MoreButton>
          )}
        </GroupFields>
      )}

      {filteredCities.length === 0 && <ErrorText>{i18n.noCitiesFound}</ErrorText>}

      <ButtonWrapper>
        <Button
          onClick={() => {
            unsubscribeChangesCitiesInHeader()
            return onSubmit(selectedCitiesState)
          }}
          uppercase={getProjectName() === 'haval'}
        >
          {i18n.applyCitiesSelection}
        </Button>
      </ButtonWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ isTitle?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: ${({ isTitle }) => {
    return !isTitle && '50px'
  }};
`

const GroupFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  overflow-y: auto;
  max-height: 50vh;

  @media screen and (max-height: 1000px) {
    max-height: 31vh;
  }
`

const Field = styled.div`
  flex: 1 0 100%;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  overflow: hidden;

  * {
    white-space: nowrap;
  }

  ${({ theme }) =>
    createMediaStyles(() => ({
      tablet: `flex: 1 0 33%; max-width: 33%; padding-right: ${theme.spacing[6]};`
    }))}
`

const InputWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  width: 100%;
`

const ErrorText = styled.div`
  text-align: center;

  color: ${({ theme }) => theme.palette.black};
  ${({ theme }) => theme.textStyles.p1};
`

const MoreButton = styled.div`
  text-decoration: underline;
  flex: 1 0 33%;

  &:hover {
    cursor: pointer;
  }
`

const ButtonWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing[8]};
`

const AllCitiesWrapper = styled.div`
  margin-right: auto;
  margin-bottom: 20px;
`

const AllCitiesLabel = styled.p`
  margin: 0;
  ${({ theme }) => theme.textStyles.p1}
  font-weight: 700;
`
